


import { Component, Vue } from "vue-property-decorator";

import { Filter } from "@Internal/Contracts";
import { AdsPublications, Records } from "App";
import { EventBus, $t } from "App/IOC";
import { MassActions, RecordActions } from "Web/Contracts";
import RecordsComponent from "Web/Components/Records.vue";
import FtpForm from "./Form.vue";
import ApiForm from "./ApiForm.vue";
import {
    Column,
    AdsPublication,
    PagerSorter,
    CustomNotification
} from "App/Models";
import { Notify } from "@/Web/Libs/Misc";

@Component({
    components: {
        Records: RecordsComponent,
        FtpForm,
        ApiForm
    }
})
export default class AdsPublicationsView extends Vue {
    $refs!: Vue["$refs"] & {
        Records: RecordsComponent,
    };

    private columns: Column[] = [];
    private records: AdsPublication[] = [];
    private row: any = null;

    /* Per-portal attributed-group counts, keyed by portal Id. The list endpoint (/gateway/list)
       omits customers, so we enrich the visible page client-side (see loadGroupCounts). */
    private groupCounts: { [id: number]: number } = {};

    private pagerSorter: PagerSorter = PagerSorter.ForColumn(
        AdsPublications.Columns.GetDefaultSort(),
        undefined,
        true
    );

    private filters = AdsPublications.Filters.Get;

    private massActions: MassActions[] = [
        {
            Id: "Actions",
            Name: $t("GLOBAL_actions"),
            ActionGroups: [
                [
                    {
                        Id: "edit",
                        ShortName: "edit",
                        Name: $t("GLOBAL_actionUpdate"),
                        Multi: false
                    },
                    {
                        Id: "delete",
                        ShortName: "delete",
                        Name: $t("GLOBAL_actionDelete"),
                        Multi: false
                    },
                    {
                        Id: "groups",
                        ShortName: "groups",
                        Name: $t("ADS_PUBLICATION_actionGroups"),
                        Multi: false
                    }
                ]
            ]
        }
    ];

    private createActions: RecordActions<AdsPublication> = [
        [
            {
                Id: "ftp",
                Title: $t( "CUSTOMERS_actionFtp" ),
                Icon: "group"
            },
            {
                Id: "api",
                Title: $t( "CUSTOMERS_actionApi" ),
                Icon: "group"
            }
        ]
    ];

    private showForm: boolean = false;
    private showApiForm: boolean = false;

    private async created(): Promise<void> {
        const [columns, error] = await AdsPublications.Columns.Get();

        if(error !== undefined) {
            EventBus.Publish("Error", error);
            return;
        }

        this.columns = columns;
    }

    private async update(
        filters: Filter[],
        pagerSorter: PagerSorter,
        resetPagination?: boolean
    ): Promise<void> {
        await this.$nextTick();
        this.$refs.Records.Loading();

        if(resetPagination)
            this.pagerSorter.Page = 1;

        const [records, updatedPagerSorter, error] =
            await AdsPublications.Find(filters, pagerSorter);
        this.records = records as AdsPublication[];
        this.pagerSorter = updatedPagerSorter;

        if(error !== undefined) {
            EventBus.Publish("Error", error);
            return;
        }

        this.loadGroupCounts(this.records);
    }

    /* /gateway/list omits customers, so fetch each visible portal's detail (which carries them)
       and cache the attributed-group count. Page size is small (10), so the extra calls are fine.
       Fired without await so the table renders immediately and counts fill in. */
    private async loadGroupCounts(records: AdsPublication[]): Promise<void> {
        await Promise.all(records.map(async (record: AdsPublication) => {
            const [portal, error] = await AdsPublications.Get(record.Id);

            if(error !== undefined || portal === undefined)
                return;

            const customers = (portal as any).customers || (portal as any).Customers || [];
            this.$set(this.groupCounts, record.Id, customers.length);
        }));
    }

    private toggleColumn(column: Column) {
        AdsPublications.Columns.Toggle(column);
    }

    /* Attributed-group count for a portal, from the client-side cache. Blank when not loaded or 0,
       otherwise the number. */
    private groupsCount(row: any): string {
        const count = this.groupCounts[row.Id];

        return count ? String(count) : "";
    }

    /* Open the dedicated full-page edit view instead of the modal: the portal form holds far too
       many fields to stay readable in a popup. Row click ("open") and the "edit" mass action both
       route here. Seed Records.Navigation so the single view's prev/next arrows walk the list. */
    private openSingle(row: AdsPublication): void {
        const urls = this.records.map(record => "/ads-publications/" + record.Id);
        const index = this.records.findIndex(record => record.Id === row.Id);

        Records.Navigation.SetList(urls, index);
        this.$router.push(`/ads-publications/${row.Id}`);
    }

    /* Open the full-page groups list. Seed Navigation with the groups URLs so the header
       prev/next arrows step between portals' groups pages. */
    private openGroups(row: AdsPublication): void {
        const urls = this.records.map(record => "/ads-publications/" + record.Id + "/groups");
        const index = this.records.findIndex(record => record.Id === row.Id);

        Records.Navigation.SetList(urls, index);
        this.$router.push(`/ads-publications/${row.Id}/groups`);
    }

    private async executeAction(id: string, rows: AdsPublication[]): Promise<void> {
        switch(id) {
            case "open":
            case "edit":
                this.openSingle(rows[0]);
                break;
            case "groups":
                this.openGroups(rows[0]);
                break;
            case "delete":
                this.row = rows[0];
                EventBus.Publish("Confirmation", {
                    Props: {
                        Message: this.$tc("ADS_PUBLICATION_deleteConfirm"),

                        Actions: [{
                            Id: "endTrial",
                            Name: this.$t("GLOBAL_actionDelete"),
                            Active: true
                        }]
                    },
                    Events: {
                        Action: async () => {
                            const [ result, error ] = 
                                await AdsPublications.DeleteGateWay( this.row.Id );
                            console.log(result);
                            if( error !== undefined ) {
                                EventBus.Publish( "Error", error );
                                return;
                            }
                            EventBus.Publish(
                                "Notification",
                                CustomNotification.Success(
                                    $t( "ADS_PUBLICATION_createSuccessTitle" ),
                                    $t( "ADS_PUBLICATION_createDeleteMessage" )
                                )
                            );
                            this.refresh();
                        }
                    }
                });

                break;

            default:
                // EventBus.Publish("UnderConstruction");
                break;
        }
    }

    private executeCreateAction(action: any): void {
        if(action.Id === "ftp") {
            this.showForm = true;
        } else if(action.Id === "api") {
            this.showApiForm = true;
        } else {
            Notify(
                undefined,
                "Missing Backend",
                "Missing Backend"
            )
        }
    }

    private refresh(): void {
        this.closeModal();
        this.update([], this.pagerSorter, true);
    }

    private closeModal(): void {
        this.showForm = false;
        this.showApiForm = false;
        this.row = null;
    }

    private get id(): string {
        return "AdsPublications";
    }
}
